import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, FileText, Lock, ScrollText, Newspaper, BookOpen, X } from "lucide-react";

const CATEGORY_META = {
  governance: { label: "GOVERNANCE", icon: ScrollText, color: "#D8AB4E" },
  "jv-agreement": { label: "JV AGREEMENT", icon: FileText, color: "#F2C568" },
  "brand-brief": { label: "BRAND BRIEF", icon: Newspaper, color: "#C1432E" },
  intel: { label: "INTEL", icon: BookOpen, color: "#10B981" },
};

const CLASS_META = {
  public: { label: "PUBLIC", color: "#A1A1AA" },
  confidential: { label: "CONFIDENTIAL", color: "#D8AB4E" },
  sovereign: { label: "SOVEREIGN", color: "#E55A45" },
};

export default function LoreLibrary() {
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({
    title: "",
    category: "governance",
    summary: "",
    classification: "confidential",
    body: "",
  });

  const load = async () => {
    const r = await api.get("/documents");
    setDocs(r.data);
  };
  useEffect(() => { load(); }, []);

  const visible = docs.filter((d) => filter === "all" || d.category === filter);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/documents", form);
      setForm({ title: "", category: "governance", summary: "", classification: "confidential", body: "" });
      setShowForm(false);
      toast.success("Tome added to the Lore Library.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/documents/${id}`);
    if (active?.id === id) setActive(null);
    toast.success("Tome stricken.");
    load();
  };

  return (
    <div className="space-y-6" data-testid="lore-library">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono text-[#D8AB4E]">MODULE 06 // LORE LIBRARY</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-1">Sovereign Document Vault</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-xl">
            Governance · JV agreements · brand briefs · intel briefings. Encrypted. Sovereign.
          </p>
        </div>
        <button data-testid="document-add-button" onClick={() => setShowForm((v) => !v)} className="btn-gold px-5 py-3 text-[11px] inline-flex items-center gap-2">
          <Plus size={14} /> {showForm ? "CLOSE" : "INDEX TOME"}
        </button>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2" data-testid="lore-filters">
        {[{ id: "all", label: "ALL" }, ...Object.entries(CATEGORY_META).map(([k, v]) => ({ id: k, label: v.label }))].map((c) => {
          const isActive = filter === c.id;
          return (
            <button
              key={c.id}
              data-testid={`lore-filter-${c.id}`}
              onClick={() => setFilter(c.id)}
              className={`px-3 py-1.5 text-[10px] font-mono-data tracking-[0.18em] border transition-all ${
                isActive ? "border-[#D8AB4E] bg-[#D8AB4E] text-[#040404]" : "border-[#D8AB4E]/20 text-zinc-400 hover:border-[#D8AB4E]/60 hover:text-[#D8AB4E]"
              }`}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {showForm && (
        <motion.form
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          onSubmit={submit}
          className="glass-panel p-6 grid md:grid-cols-12 gap-4"
          data-testid="document-form"
        >
          <div className="md:col-span-6">
            <label className="label-mono text-zinc-400 mb-2 block">Title</label>
            <input
              data-testid="document-title-input"
              required value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#D8AB4E]/30 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D8AB4E]"
              placeholder="Sovereign Memo — Q2 Doctrine"
            />
          </div>
          <div className="md:col-span-3">
            <label className="label-mono text-zinc-400 mb-2 block">Category</label>
            <select
              data-testid="document-category-select"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#D8AB4E]/30 px-3 py-2.5 text-sm"
            >
              {Object.entries(CATEGORY_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="label-mono text-zinc-400 mb-2 block">Classification</label>
            <select
              data-testid="document-class-select"
              value={form.classification}
              onChange={(e) => setForm({ ...form, classification: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#D8AB4E]/30 px-3 py-2.5 text-sm"
            >
              {Object.entries(CLASS_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-12">
            <label className="label-mono text-zinc-400 mb-2 block">Summary</label>
            <input
              data-testid="document-summary-input"
              required value={form.summary}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#D8AB4E]/30 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D8AB4E]"
              placeholder="One-line abstract"
            />
          </div>
          <div className="md:col-span-12">
            <label className="label-mono text-zinc-400 mb-2 block">Body</label>
            <textarea
              data-testid="document-body-input"
              rows={5}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#D8AB4E]/30 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D8AB4E] scrollbar-thin"
              placeholder="Full text of the doctrine…"
            />
          </div>
          <div className="md:col-span-12 flex justify-end">
            <button data-testid="document-submit-button" className="btn-gold px-6 py-2.5 text-[11px]">SEAL TOME</button>
          </div>
        </motion.form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.length === 0 && (
          <div className="md:col-span-3 glass-panel p-10 text-center text-zinc-500 font-mono-data text-sm">
            No tomes match this filter.
          </div>
        )}
        {visible.map((d) => {
          const meta = CATEGORY_META[d.category];
          const cl = CLASS_META[d.classification];
          const Icon = meta.icon;
          return (
            <motion.button
              key={d.id}
              whileHover={{ y: -3 }}
              onClick={() => setActive(d)}
              className="glass-panel p-5 relative grain text-left hover:border-[#D8AB4E]/45 transition-colors"
              data-testid={`document-${d.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="h-9 w-9 grid place-items-center border" style={{ borderColor: meta.color + "40" }}>
                  <Icon size={14} style={{ color: meta.color }} />
                </div>
                <div className="px-2 py-1 border text-[9px] font-mono-data tracking-[0.18em] inline-flex items-center gap-1"
                     style={{ borderColor: cl.color + "55", color: cl.color }}>
                  <Lock size={10} /> {cl.label}
                </div>
              </div>
              <div className="font-display text-lg mt-4">{d.title}</div>
              <div className="label-mono text-zinc-500 mt-0.5" style={{ color: meta.color }}>{meta.label}</div>
              <div className="text-zinc-400 text-xs mt-3 line-clamp-2 font-light">{d.summary}</div>
              <div className="mt-4 pt-3 border-t border-[#D8AB4E]/10 flex items-center justify-between font-mono-data text-[10px] text-zinc-500">
                <span>UPDATED {new Date(d.updated_at).toLocaleDateString()}</span>
                <span className="text-[#D8AB4E]">OPEN →</span>
              </div>
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setActive(null)}
            data-testid="document-modal"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="glass-panel-strong max-w-2xl w-full max-h-[85vh] overflow-y-auto scrollbar-thin relative grain"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between p-5 border-b border-[#D8AB4E]/15 bg-[#040404]/95 backdrop-blur">
                <div className="flex items-center gap-3">
                  <div className="px-2 py-1 border text-[9px] font-mono-data tracking-[0.18em]"
                       style={{ borderColor: CLASS_META[active.classification].color + "55", color: CLASS_META[active.classification].color }}>
                    {CLASS_META[active.classification].label}
                  </div>
                  <div className="label-mono text-[#D8AB4E]">{CATEGORY_META[active.category].label}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid="document-modal-delete"
                    onClick={() => remove(active.id)}
                    className="h-8 w-8 grid place-items-center border border-zinc-800 text-zinc-500 hover:text-[#E55A45] hover:border-[#C1432E]/50 transition-colors"
                  >
                    <Trash2 size={13} />
                  </button>
                  <button data-testid="document-modal-close" onClick={() => setActive(null)} className="h-8 w-8 grid place-items-center border border-[#D8AB4E]/30 text-[#D8AB4E]">
                    <X size={14} />
                  </button>
                </div>
              </div>
              <div className="p-7">
                <h3 className="font-display text-3xl">{active.title}</h3>
                <div className="font-mono-data text-[10px] text-zinc-500 tracking-[0.2em] mt-2">
                  UPDATED {new Date(active.updated_at).toLocaleString()}
                </div>
                <div className="gold-divider my-6" />
                <p className="text-zinc-300 leading-relaxed">{active.summary}</p>
                {active.body && (
                  <pre className="mt-6 whitespace-pre-wrap text-zinc-400 text-sm leading-relaxed font-mono-data">{active.body}</pre>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
